import { useMe, useOrganization } from '@/lib/queries';
import { AppShell, type NavItem } from '@/components/AppShell';
import { ConnectGate } from '@/features/connect/ConnectGate';
import { LicensingPanel } from '@/features/licensing/LicensingPanel';
import { AdoptionPanel } from '@/features/adoption/AdoptionPanel';
import { OversharingScan } from '@/features/oversharing/OversharingScan';
import { LabelCoveragePanel } from '@/features/labels/LabelCoveragePanel';
import { SiteOwnersPanel } from '@/features/siteOwners/SiteOwnersPanel';
import { GuestAccessPanel } from '@/features/guests/GuestAccessPanel';
import { ComplianceDashboard } from '@/features/compliance/ComplianceDashboard';
import { QueryLog } from '@/features/log/QueryLog';
import { ExecSummary } from '@/features/summary/ExecSummary';

const NAV: NavItem[] = [
  { id: 'licensing', label: 'Licensing' },
  { id: 'c3', label: 'Adoption' },
  { id: 'c4', label: 'Oversharing' },
  { id: 'labels', label: 'Labels' },
  { id: 'c14', label: 'Site owners' },
  { id: 'guests', label: 'Guest access' },
  { id: 'audit', label: 'Compliance' },
  { id: 'log', label: 'Query log' },
  { id: 'c6', label: 'Summary' },
];

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

function Dashboard() {
  const me = useMe();
  const org = useOrganization();
  const tenantName = org.data?.displayName;
  const tenantId = org.data?.id;
  const adminName = me.data?.displayName ?? me.data?.userPrincipalName;

  return (
    <AppShell
      tenantName={tenantName}
      adminName={adminName}
      nav={NAV}
      onNavigate={scrollTo}
    >
      <section className="hero frost">
        <span className="hero__eyebrow">Microsoft&nbsp;365 posture · security · compliance · zero-egress</span>
        <h1 className="hero__h1">Audit, secure, and prove your Microsoft&nbsp;365 tenant.</h1>
        <p className="hero__lede">
          One place to review identity, device, and application posture; Copilot readiness and
          oversharing; licence allocation; sensitivity-label coverage; and to gather auditor-ready
          compliance evidence mapped to CIS, NIST, ISO&nbsp;27001, SOC&nbsp;2 and HIPAA. Every module
          reads only what it needs, only when you grant it — using your own delegated admin token,
          entirely in this browser.
        </p>
      </section>

      <LicensingPanel />
      <AdoptionPanel />
      <OversharingScan />
      <LabelCoveragePanel />
      <SiteOwnersPanel />
      <GuestAccessPanel />
      <ComplianceDashboard tenantName={tenantName} tenantId={tenantId} adminName={adminName} />
      <QueryLog />
      <ExecSummary tenantName={tenantName} adminName={adminName} />
    </AppShell>
  );
}

export default function App() {
  return (
    <ConnectGate>
      <Dashboard />
    </ConnectGate>
  );
}
